import { useId, type ReactNode } from 'react';
import { Box, InputAdornment, TextField, type TextFieldProps } from '@mui/material';
import { Field } from './Field';
import { HelpPopover } from './HelpPopover';

/** 라벨 연결을 유지한 채 시각적 공간만 제거합니다. */
const visuallyHiddenLabelStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

export type FormTextFieldProps = Omit<TextFieldProps<'outlined'>, 'label' | 'variant'> & {
  /**
   * 입력창 위에 고정해서 보여주는 접근 가능한 라벨입니다.
   * 고정 라벨이 있는 날짜 필터에도 사용합니다. 일반 검색에는 SearchField를,
   * 헤더에서 한 줄로 표시하는 조회 기준일에는 CompactDateField를 사용합니다.
   */
  label: ReactNode;
  /** 입력 오류·필수 제약과 분리한 선택적 도움말입니다. */
  help?: ReactNode;
  /** 짧은 도구줄에서 라벨을 시각적으로 숨깁니다. 접근 가능한 이름과 보조 작업은 유지합니다. */
  hideLabel?: boolean;
  /** 라벨 오른쪽에 보조 설명이나 짧은 action을 배치합니다. */
  labelSuffix?: ReactNode;
  /** feature가 입력값의 실제 검증 기준에 맞춰 계산한 글자 수입니다. 입력 길이를 제한하지 않습니다. */
  characterCount?: { current: number; max?: number };
  /** 오른쪽에 표시하는 단위입니다. 여백·글꼴·접근성 설명은 공용 입력 계약이 소유합니다. */
  unit?: string;
};

/**
 * 편집 form을 위한 입력 패턴입니다. MUI의 floating label을 사용하지 않고 라벨과 입력 영역을
 * 분리해, text·select·date·multiline field가 같은 수직 리듬을 유지합니다.
 */
export function FormTextField({
  id,
  label,
  help,
  hideLabel = false,
  labelSuffix,
  characterCount,
  unit,
  helperText,
  required = false,
  error = false,
  disabled = false,
  fullWidth = true,
  size = 'small',
  slotProps,
  sx,
  ...textFieldProps
}: FormTextFieldProps) {
  const generatedId = useId();
  const inputId = id ?? `form-field-${generatedId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const labelId = `${inputId}-label`;
  const resolvedHelperText = characterCount === undefined ? helperText : (
    <Box component="span" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
      <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{helperText}</Box>
      <Field.Count invalid={characterCount.max !== undefined && characterCount.current > characterCount.max}>
        {characterCount.current.toLocaleString()}{characterCount.max === undefined ? '자' : ` / ${characterCount.max.toLocaleString()}자`}
      </Field.Count>
    </Box>
  );
  const inputSlotProps = slotProps?.input;
  const withUnit = (props: Exclude<typeof inputSlotProps, Function> = {}) => ({
    ...props,
    'aria-describedby': [props['aria-describedby'], resolvedHelperText ? `${inputId}-helper-text` : undefined, `${inputId}-unit`].filter(Boolean).join(' '),
    endAdornment: <>{props.endAdornment}<InputAdornment position="end" disablePointerEvents disableTypography id={`${inputId}-unit`}>{unit}</InputAdornment></>,
  });
  const mergedInputSlotProps = unit === undefined ? inputSlotProps : typeof inputSlotProps === 'function'
    ? (ownerState: Parameters<typeof inputSlotProps>[0]) => withUnit(inputSlotProps(ownerState))
    : withUnit(inputSlotProps);
  const htmlInputSlotProps = slotProps?.htmlInput;
  const withUnitDescription = (props: Exclude<typeof htmlInputSlotProps, Function> = {}) => props['aria-describedby'] === undefined
    ? props
    : {
        ...props,
        'aria-describedby': [props['aria-describedby'], resolvedHelperText ? `${inputId}-helper-text` : undefined, `${inputId}-unit`].filter(Boolean).join(' '),
      };
  const mergedHtmlInputSlotProps = unit === undefined ? htmlInputSlotProps : typeof htmlInputSlotProps === 'function'
    ? (ownerState: Parameters<typeof htmlInputSlotProps>[0]) => withUnitDescription(htmlInputSlotProps(ownerState))
    : withUnitDescription(htmlInputSlotProps);
  const selectSlotProps = slotProps?.select;
  const mergedSelectSlotProps = typeof selectSlotProps === 'function'
    ? (ownerState: Parameters<typeof selectSlotProps>[0]) => ({
        ...selectSlotProps(ownerState),
        labelId,
      })
    : {
        ...selectSlotProps,
        labelId,
      };

  return (
    <Field.Root controlId={inputId} required={required} disabled={disabled} invalid={error} spacing={hideLabel && labelSuffix === undefined ? 0 : undefined} sx={sx}>
      <Field.Header sx={hideLabel && labelSuffix === undefined ? visuallyHiddenLabelStyles : undefined}>
        <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center' }}>
          <Field.Label sx={hideLabel && labelSuffix !== undefined ? visuallyHiddenLabelStyles : undefined}>{label}</Field.Label>
          {help === undefined || hideLabel ? null : <HelpPopover title={typeof label === 'string' ? label : '입력 안내'}>{help}</HelpPopover>}
        </Box>
        {labelSuffix !== undefined ? <Field.Suffix>{labelSuffix}</Field.Suffix> : null}
      </Field.Header>
      <Field.Control>
        <TextField
          {...textFieldProps}
          id={inputId}
          required={required}
          error={error}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          variant="outlined"
          helperText={resolvedHelperText}
          slotProps={{
            ...slotProps,
            input: mergedInputSlotProps,
            htmlInput: mergedHtmlInputSlotProps,
            select: mergedSelectSlotProps,
          }}
        />
      </Field.Control>
    </Field.Root>
  );
}
